/**
 * The report's "left and right at a glance" page: each foot measurement as
 * one row — the left foot's value and grade, the measurement and its normal
 * range between them, the right foot's — the way a clinician explains a foot
 * scan to the patient standing beside it.
 *
 * Only what this scan measured: a row with neither foot's value is left out.
 * The grades are the report's own (metricBands.js), never decided again here.
 * Pure, for tests.
 */

import { rangeForAngle, severityForAngle } from '../metricBands';
import { referenceText } from './reference';
import { ARCH_HIGH_MAX, ARCH_NORMAL_MAX, shoeSizeOf, widthClassOf } from './plantarModel';
import { isUnmeasured, legPosesOf, postureViewsOf } from './scanModel';

export const GRADE_TEXT = { normal: 'Normal', mild: 'Mild', moderate: 'Moderate', severe: 'Severe' };

/**
 * An arch index graded as the reference podoscope reports do: High below
 * 0.21, Normal to 0.26, Low above; Low (+) under 0.28, (++) under 0.30, (+++)
 * beyond, and High the same way below 0.21.
 */
export function archGrade(index) {
  if (typeof index !== 'number' || !(index > 0)) return null;
  if (index > ARCH_HIGH_MAX && index < ARCH_NORMAL_MAX) return { text: 'Normal', severity: 'normal' };
  const low = index >= ARCH_NORMAL_MAX;
  const off = low ? index - ARCH_NORMAL_MAX : ARCH_HIGH_MAX - index;
  const [plus, severity] = off < 0.02 ? ['+', 'mild'] : off < 0.04 ? ['++', 'moderate'] : ['+++', 'severe'];
  return { text: `${low ? 'Low' : 'High'} (${plus})`, severity };
}

const mm = v => (typeof v === 'number' && v > 0 ? `${Math.round(v)}` : null);
const side = (value, grade = null, severity = null) => (value == null ? null : { value, grade, severity });

/** One angle row from a pair of stored angles ("Left …" / "Right …"), graded as stored. */
function angleRow(id, name, left, right, note = null) {
  const one = a => (a && Number.isFinite(a.degrees)
    ? side(`${a.degrees.toFixed(1)}°`, GRADE_TEXT[severityForAngle(a)], severityForAngle(a)) : null);
  const range = rangeForAngle(left ?? right);
  return { id, name, unit: 'deg', left: one(left), right: one(right), reference: note ?? (range ? referenceText(range) : null) };
}

/** A heel or shin finding as a side: its angle, which way it turns, and its grade. */
function findingSide(f, rangeName, words) {
  if (!f || !Number.isFinite(f.degrees)) return null;
  const sev = severityForAngle({ name: rangeName, degrees: f.degrees });
  const way = words[f.finding] ?? '';
  return side(`${Math.abs(f.degrees).toFixed(1)}°${way ? ` ${way}` : ''}`, GRADE_TEXT[sev], sev);
}

/** Every measurement of the scan with a left and a right, in the order a foot exam reads. */
export function footComparisonOf(scan) {
  if (!scan) return [];
  const rows = [];
  const mat = scan.sensorAnalytics?.feet ?? null;
  const matSized = Boolean(mat?.left?.footLengthMm || mat?.right?.footLengthMm);
  const cam = scan.footSize ?? null;
  const foot = s => (matSized ? mat?.[s] : cam?.[s]) ?? null;
  // A camera width is the foot's own only from its outline (plantarModel).
  const ownWidth = s => (matSized ? true : foot(s)?.measuredFrom === 'outline');

  // ── Size ─────────────────────────────────────────────────────────────
  const length = s => mm(foot(s)?.footLengthMm);
  rows.push({ id: 'length', name: 'Foot length', unit: 'mm', reference: 'heel to longest toe',
    left: side(length('left')), right: side(length('right')) });
  const shoe = s => {
    const v = shoeSizeOf(foot(s)?.footLengthMm);
    return v ? side(v.replace(/^EU\s*/, '').replace(/\s*\/\s*UK\s*/, ' · UK ')) : null;
  };
  rows.push({ id: 'shoe', name: 'Shoe size (by foot length)', unit: 'EU · UK', reference: 'a guide: try shoes on', left: shoe('left'), right: shoe('right') });
  const width = s => {
    const f = foot(s);
    if (!ownWidth(s) || !mm(f?.footWidthMm)) return null;
    const c = widthClassOf(f.footWidthMm, f.footLengthMm);
    return side(mm(f.footWidthMm), c ? c[0].toUpperCase() + c.slice(1) : null, c === 'standard' ? 'normal' : c ? 'mild' : null);
  };
  rows.push({ id: 'width', name: 'Foot width', unit: 'mm', reference: 'Narrow < standard < Wide, for its length', left: width('left'), right: width('right') });

  // ── Arch ─────────────────────────────────────────────────────────────
  const arch = s => {
    const ai = (matSized || mat?.[s]?.archIndex ? mat?.[s]?.archIndex : null) ?? scan.cameraPlantar?.[s]?.archIndex;
    const g = archGrade(ai);
    return g ? side(ai.toFixed(2), g.text, g.severity) : null;
  };
  rows.push({ id: 'arch', name: 'Arch index', unit: 'ratio', left: arch('left'), right: arch('right'),
    reference: `High < ${ARCH_HIGH_MAX}–${ARCH_NORMAL_MAX} Normal < Low · Low (+) < 0.28, (++) < 0.30, (+++) beyond` });

  // ── Alignment ────────────────────────────────────────────────────────
  const stand = legPosesOf(scan).find(p => p.pose === 'stand' && !isUnmeasured(p)) ?? null;
  const fs = stand?.findings ?? null;
  if (fs) {
    rows.push({ id: 'heel', name: 'Heel angle (rearfoot)', unit: 'deg',
      left: findingSide(fs.heelLeft, 'Left calcaneal angle', { valgus: 'Eversion', varus: 'Inversion' }),
      right: findingSide(fs.heelRight, 'Right calcaneal angle', { valgus: 'Eversion', varus: 'Inversion' }),
      reference: referenceText(rangeForAngle({ name: 'Left calcaneal angle' })) });
    rows.push({ id: 'leg', name: 'Leg angle (shin rotation)', unit: 'deg',
      left: findingSide(fs.shinLeft, 'Left shin rotation', { 'internal-rotation': 'Internal', 'external-rotation': 'External' }),
      right: findingSide(fs.shinRight, 'Right shin rotation', { 'internal-rotation': 'Internal', 'external-rotation': 'External' }),
      reference: referenceText(rangeForAngle({ name: 'Left shin rotation' })) });
  }
  const angles = postureViewsOf(scan).filter(v => !isUnmeasured(v)).flatMap(v => v.angles ?? []);
  const pair = base => [angles.find(a => a?.name === `Left ${base}`), angles.find(a => a?.name === `Right ${base}`)];
  const [kl, kr] = pair('knee deviation');
  if (kl || kr) rows.push(angleRow('knee', 'Knee alignment', kl, kr));
  const [pl, pr] = pair('foot progression');
  if (pl || pr) rows.push(angleRow('progression', 'Foot angle (toe-out)', pl, pr));

  // ── Load ─────────────────────────────────────────────────────────────
  const b = scan.sensorAnalytics?.bilateral ?? scan.cameraPlantar?.bilateral ?? null;
  if (typeof b?.left === 'number' && typeof b?.right === 'number') {
    const grade = v => (Math.abs(v - 50) <= 5 ? ['Normal', 'normal'] : Math.abs(v - 50) <= 10 ? ['Mild', 'mild'] : ['Moderate', 'moderate']);
    const one = v => side(`${Math.round(v)} %`, ...grade(v));
    rows.push({ id: 'load', name: 'Weight on each foot', unit: '%', left: one(b.left), right: one(b.right),
      reference: scan.sensorAnalytics?.bilateral ? '50 ± 5 % each (pressure mat)' : '50 ± 5 % each (optical estimate)' });
  }

  return rows.filter(r => r.left || r.right);
}
